import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import { AssetBadge, Badge, NetworkBadge } from '../ui';
import { formatUsd, shortAddress } from '../../lib/format';

export type TxKind = 'deposit' | 'withdrawal';
export type TxStatus =
  | 'pending'
  | 'confirming'
  | 'done'
  | 'failed'
  | 'rejected';

interface Props {
  kind: TxKind;
  chainId: number;
  asset: string;
  /** Sent amount (what the user sent / requested). */
  amountUsd: string | number;
  /**
   * Actual credited / executed amount. Optional — for deposits this
   * appears once swap_executor finishes; for withdrawals it's the
   * post-fee net delivered to the destination.
   */
  creditedUsd?: string | number | null;
  status: TxStatus;
  /** ISO 8601 timestamp. */
  createdAt: string;
  /** Destination address — withdrawals only. */
  toAddress?: string | null;
  /** Confirming status — show progress (3/12 confirmations). */
  confirmations?: { current: number; required: number };
  /** Optional rejection reason — surfaced as an inline note. */
  rejectionReason?: string | null;
  locale?: string;
}

/**
 * Single row in deposit / withdrawal history. Information density was
 * a recurring complaint — this layout surfaces network + amount + sent
 * vs credited + status in one block without leaning on a generic
 * <ActivityRow> that hid half the fields.
 */
export function TxHistoryRow({
  kind,
  chainId,
  asset,
  amountUsd,
  creditedUsd,
  status,
  createdAt,
  toAddress,
  confirmations,
  rejectionReason,
  locale = 'en-US',
}: Props) {
  const { t } = useTranslation();
  const sign = kind === 'deposit' ? '+' : '−';
  const created = new Date(createdAt);
  const dateLabel = created.toLocaleDateString(locale, {
    day: '2-digit',
    month: 'short',
  });
  const timeLabel = created.toLocaleTimeString(locale, {
    hour: '2-digit',
    minute: '2-digit',
  });
  const credited = creditedUsd != null ? Number(creditedUsd) : null;
  const sentAmount = Number(amountUsd);
  const showCredited =
    credited !== null && Number.isFinite(credited) && credited !== sentAmount;

  return (
    <div className="px-4 py-3.5">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <NetworkBadge chainId={chainId} size={36} />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <span className="pb-numeral text-[15px] font-medium text-tg-text">
                {sign}
                {formatUsd(sentAmount)}
              </span>
              <span className="inline-flex items-center gap-1 text-[12px] text-tg-hint">
                <AssetBadge symbol={asset} size={14} />
                {asset}
              </span>
            </div>
            <p className="pb-caption mt-0.5 truncate">
              {dateLabel} · {timeLabel}
              {toAddress ? (
                <>
                  {' · '}
                  <span className="font-mono">{shortAddress(toAddress)}</span>
                </>
              ) : null}
            </p>
            {showCredited ? (
              <p className="pb-caption mt-0.5">
                {t('history.credited')}:{' '}
                <span className="font-medium text-tg-text">
                  {formatUsd(credited!)}
                </span>
              </p>
            ) : null}
            {rejectionReason ? (
              <p className="mt-1 text-[12px] text-pb-danger">{rejectionReason}</p>
            ) : null}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1.5 shrink-0">
          <StatusPill status={status} confirmations={confirmations} />
        </div>
      </div>
    </div>
  );
}

function StatusPill({
  status,
  confirmations,
}: {
  status: TxStatus;
  confirmations?: { current: number; required: number };
}) {
  const { t } = useTranslation();
  const tone =
    status === 'done'
      ? 'success'
      : status === 'failed' || status === 'rejected'
        ? 'danger'
        : 'warn';
  const label =
    status === 'confirming' && confirmations
      ? t('history.status.confirming_x_y', confirmations)
      : t(`history.status.${status}`);
  return (
    <Badge tone={tone} dot className={clsx(status === 'pending' && 'pb-pulse')}>
      {label}
    </Badge>
  );
}
